import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { pluralReadableTypeName } from '@iotv/core-services';
import { Milliseconds, RangeEntity } from '@iotv/datamodel';
import { isNonNaNNumber } from '@iotv/iotv-v3-types';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { timestampToISODateTime } from '../../../helpers/format';
import { useNormals } from '../../../hooks/useNormals';
import { useObjectHistory } from '../../../hooks/useObjectHistory';
import { HistoryLineChart } from '../../../v2/components/charts/HistoryLineChart';
import ObjectCard from '../../../v2/components/generic/ObjectView/ObjectCard';
import { EntityGroupView } from '../../SytemManagement/Entity/EntityGroupViewV2';
export const RangeEntityView = (props) => _jsx(ObjectCard, { ...props, additionalTabs: { Summary: EntitySummary } });
const EntitySummary = (props) => {
    const { viewObject, isSmallView } = props;
    const range = viewObject.matchedPrimary ? new RangeEntity(viewObject.matchedPrimary) : undefined;
    const userAttributes = range?.getUserAttributes();
    const rangeStartAttributes = userAttributes?.['rangeStart'];
    const distanceAttributes = userAttributes?.['distance'];
    const rangeEndAttributes = userAttributes?.['rangeEnd'];
    const [objectHistoryFilter] = useState({
        recentStateHistory: {
            lastInNMs: Milliseconds.DAY,
            lastN: undefined,
        },
    });
    const [objectHistory] = useObjectHistory(range, objectHistoryFilter);
    const [normals] = useNormals(range);
    const rangeStart = rangeStartAttributes?.displayFn?.(range?.rangeStart) ?? range?.rangeStart;
    const startLabel = typeof rangeStartAttributes?.label === 'function' ? rangeStartAttributes.label() : rangeStartAttributes?.label;
    const reading = distanceAttributes?.displayFn?.(range?.distance) ?? range?.distance;
    const readingLabel = typeof distanceAttributes?.label === 'function' ? distanceAttributes.label() : distanceAttributes?.label;
    const rangeEnd = rangeEndAttributes?.displayFn?.(range?.rangeEnd) ?? range?.rangeEnd;
    const endLabel = typeof rangeEndAttributes?.label === 'function' ? rangeEndAttributes.label() : rangeEndAttributes?.label;
    const hasConfig = [rangeStart, reading, rangeEnd].every((v) => v !== undefined);
    const config = hasConfig ? (_jsx(DistanceRange, { min: rangeStart, minUnit: rangeStartAttributes?.units, minLabel: startLabel, reading: reading, readingUnit: distanceAttributes?.units, readingLabel: readingLabel, max: rangeEnd, maxUnit: rangeEndAttributes?.units, maxLabel: endLabel })) : (_jsx(_Fragment, {}));
    const distanceLabel = typeof distanceAttributes?.label === 'function' ? distanceAttributes.label() : distanceAttributes?.label;
    const distance = distanceLabel ? (_jsx(StatCard, { title: distanceLabel, children: _jsxs(Typography, { fontWeight: "bold", children: [distanceAttributes?.displayFn?.(range?.distance) ?? range?.distance, " ", distanceAttributes?.units] }) })) : (_jsx(_Fragment, {}));
    const lastUpdate = objectHistory?.recentStateHistory?.lastTimestamp() ? timestampToISODateTime(objectHistory?.recentStateHistory?.lastTimestamp()) : null;
    const lastUpdated = lastUpdate ? (_jsx(StatCard, { title: "Last updated", children: _jsx(Typography, { fontWeight: "bold", children: lastUpdate }) })) : (_jsx(_Fragment, {}));
    const batteryPercentage = range?.batteryPercentage;
    const battery = isNonNaNNumber(batteryPercentage) ? (_jsx(StatCard, { title: "Battery", children: _jsxs(Typography, { fontWeight: "bold", children: [batteryPercentage, "\u00A0%"] }) })) : (_jsx(_Fragment, {}));
    const startExists = rangeStart !== undefined;
    const readingExists = reading !== undefined;
    const endExists = rangeEnd !== undefined;
    const minChartableValue = startExists && readingExists ? Math.min(reading, rangeStart) : undefined;
    const maxChartableValue = endExists && readingExists ? Math.max(reading, rangeEnd) : undefined;
    const chart = objectHistory?.recentStateHistory !== undefined ? (_jsx(HistoryLineChart, { historyObject: objectHistory?.recentStateHistory, normals: normals, keys: ['distance'], normalsAccumulation: { distance: 14 }, userAttributes: userAttributes, showAxisLabels: true, range: minChartableValue !== undefined && maxChartableValue !== undefined ? [minChartableValue, maxChartableValue] : undefined })) : null;
    const graph = (_jsx(Stack, { direction: "row", flex: 1, children: chart }));
    return (_jsxs(Stack, { height: isSmallView ? '200cqh' : 'calc(100cqh - 8em)', direction: isSmallView ? 'column' : 'row', children: [config, _jsxs(Stack, { flex: 2, children: [_jsxs(Stack, { height: '8em', direction: isSmallView ? 'column' : 'row', paddingInline: 2, justifyContent: "space-evenly", gap: 2, children: [distance, lastUpdated, battery] }), graph] })] }));
};
const DistanceRange = ({ min, minUnit, minLabel, reading, readingUnit, readingLabel, max, maxUnit, maxLabel }) => {
    const minFormatted = minUnit ? `${min} ${minUnit}` : min;
    const readingFormatted = readingUnit ? `${reading} ${readingUnit}` : reading;
    const maxFormatted = maxUnit ? `${max} ${maxUnit}` : max;
    const solidBorder = {
        borderTopColor: (t) => t.palette.text.primary,
        borderTopWidth: '2px',
    };
    const dashedBorder = {
        ...solidBorder,
        borderTopStyle: 'dashed',
    };
    const solidStyle = {
        ['::before']: solidBorder,
        ['::after']: solidBorder,
    };
    const dashedStyle = {
        ['::before']: dashedBorder,
        ['::after']: dashedBorder,
    };
    const solidLine = {
        borderRightColor: (t) => t.palette.text.primary,
        borderRightWidth: '0.15em',
    };
    const dashedLine = {
        ...solidLine,
        borderRightStyle: 'dashed',
    };
    const minPosition = min !== undefined && max !== undefined ? min / max : 0;
    const minStyle = {
        ...solidStyle,
        position: 'absolute',
        top: `${minPosition * 100}%`,
        left: 0,
        right: 0,
    };
    const minLine = (_jsx(Divider, { textAlign: "center", sx: minStyle, children: _jsx(Chip, { label: minLabel + ': ' + minFormatted, size: "small" }) }));
    const readingPosition = reading !== undefined && max !== undefined ? reading / max : 0.5;
    const readingStyle = {
        ...dashedStyle,
        position: 'absolute',
        top: `${readingPosition * 100}%`,
        left: 0,
        right: 0,
    };
    const readingLine = (_jsx(Divider, { textAlign: "right", sx: readingStyle, children: _jsx(Chip, { label: readingLabel, size: "small" }) }));
    const readingArrow = (_jsx(Box, { sx: { position: 'absolute', top: 0, right: '30%', bottom: `calc(${(1 - readingPosition) * 100}% - 0.5625em)` }, children: _jsxs(Stack, { height: "100%", direction: "row", children: [_jsx(Stack, { justifyContent: "center", children: _jsx(Typography, { children: readingFormatted }) }), _jsxs(Stack, { flex: 1, justifyContent: "space-between", alignItems: "center", children: [_jsx(ArrowUpwardIcon, {}), _jsx(Stack, { flex: 1, direction: "row", marginBlock: -1, children: _jsx(Divider, { orientation: "vertical", flexItem: true, sx: dashedLine }) }), _jsx(ArrowDownwardIcon, {})] })] }) }));
    const maxStyle = {
        ...solidStyle,
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
    };
    const maxLine = (_jsx(Divider, { textAlign: "left", sx: maxStyle, children: _jsx(Chip, { label: maxLabel, size: "small" }) }));
    const maxArrow = (_jsx(Box, { sx: { position: 'absolute', top: 0, left: '30%', bottom: '-0.5625em' }, children: _jsxs(Stack, { height: "100%", direction: "row", children: [_jsxs(Stack, { flex: 1, justifyContent: "space-between", alignItems: "center", children: [_jsx(ArrowUpwardIcon, {}), _jsx(Stack, { flex: 1, direction: "row", marginBlock: -1, children: _jsx(Divider, { orientation: "vertical", flexItem: true, sx: solidLine }) }), _jsx(ArrowDownwardIcon, {})] }), _jsx(Stack, { justifyContent: "center", children: _jsx(Typography, { children: maxFormatted }) })] }) }));
    return (_jsxs(Box, { flex: 1, position: "relative", marginBlockEnd: 2, children: [minLine, readingLine, readingArrow, maxLine, maxArrow] }));
};
const StatCard = ({ title, children }) => {
    return (_jsx(Box, { flex: 1, padding: 2, borderRadius: 2, sx: { backgroundColor: (t) => t.alpha(t.palette.primary.light, 0.5) }, children: _jsxs(Stack, { height: "100%", gap: 1, children: [_jsxs(Typography, { variant: "h6", children: [title, ":"] }), _jsx(Stack, { flex: 1, direction: "row", justifyContent: "center", alignItems: "center", children: children })] }) }));
};
const GroupTabName = pluralReadableTypeName('Range');
export const RangeEntityGroup = (props) => _jsx(EntityGroupView, { ...props, additionalTabs: { [GroupTabName]: GroupStatus } });
const GroupStatus = (props) => {
    return _jsx(_Fragment, {});
};
